/* ================================================================
   AISTROWISE — Mapa de astrocartografía (deferred, no libraries)
   Clone of the aw-calculadora wizard pointed at the public
   astrocartography endpoint; renders planet lines on an inline
   equirectangular world map (x = (lon + 180) / 360 * 720,
   y = 20 + (90 - lat) / 180 * 360, real Natural Earth geometry).
   MC/IC are vertical meridian lines. ASC/DSC follow the horizon
   curves from lines.horizon_curves when the API sends them, split
   at the ±180° antimeridian; if horizon_curves is absent they fall
   back to the vertical meridian rendering so the page never breaks.

   SECURITY (non-negotiable):
   - Calls the PUBLIC endpoint POST api.aistrowise.com/astrocartography-lines.
   - Sends NO credentials: no X-API-Key, no cookies (credentials:'omit').
   - date + time are mandatory (no unknown-hour path); validated client-side.
   - Birth data is sent ONLY to api.aistrowise.com. It is never stored
     (no localStorage), never logged (no console), and never attached to
     the astromapa_complete analytics event.
   ================================================================ */
(function () {
  'use strict';
  // Plan inglés (2026-10-20): texto de cliente por awT (identidad en español, aw-dict-en.js en inglés).
  var T = (typeof window !== 'undefined' && window.awT) || function (s) { return s; };
  function isEn() { return typeof window !== 'undefined' && window.awLang === 'en'; }
  // Plan francés (2026-10-21): el idioma de la página como parámetro. Español sin cambios (fuera de estas tablas).
  function lang() { return (typeof window !== 'undefined' && window.awLang) || 'es'; }
  function own(o, k) { return Object.prototype.hasOwnProperty.call(o, k) ? o[k] : null; }
  // Display codes of the angles per glossary: English AC and DC, French AS, DS and FC. The data keeps MC/IC/ASC/DSC.
  var ANGLE_CODES = { en: { ASC: 'AC', DSC: 'DC' }, fr: { IC: 'FC', ASC: 'AS', DSC: 'DS' } };
  function angleLabel(code) { var m = own(ANGLE_CODES, lang()); return (m && own(m, code)) || code; }
  // Birth date in the result lead: English "March 14, 1990", French "14 mars 1990" (1er for the first day).
  var DATE_FMT = { en: '{m} {d}, {y}', fr: '{d} {m} {y}' };

  // Canonical planet order + display data. `keys` are normalized (lowercase,
  // no accents) and cover both the API's English names and the Spanish ones.
  // `short` feeds the map legend; `meaning` feeds the per-planet list rows.
  // `es` and `short` stay plain literals (tests/aw-globe-2026-09-16 reads this table): awT at the display sites.
  var PLANETS = [
    { keys: ['sun', 'sol'],           es: 'Sol',      glyph: '\u2609', color: '#E5C36B', meaning: 'Señala energía y reconocimiento.', short: 'energía y reconocimiento' },
    { keys: ['moon', 'luna'],         es: 'Luna',     glyph: '☽', color: '#D7D3FF', meaning: 'Muestra hogar y emociones.', short: 'hogar y emociones' },
    { keys: ['mercury', 'mercurio'],  es: 'Mercurio', glyph: '☿', color: '#7FD1C8', meaning: 'Apunta a comunicación e ideas.', short: 'comunicación' },
    { keys: ['venus'],                es: 'Venus',    glyph: '♀', color: '#E79BC5', meaning: 'Señala amor y relaciones.', short: 'amor y vínculos' },
    { keys: ['mars', 'marte'],        es: 'Marte',    glyph: '♂', color: '#E06C5A', meaning: 'Muestra acción e impulso.', short: 'acción e impulso' },
    { keys: ['jupiter'],              es: 'Júpiter',  glyph: '♃', color: '#8FA9F2', meaning: 'Apunta a expansión y oportunidades.', short: 'expansión y aprendizaje' },
    { keys: ['saturn', 'saturno'],    es: 'Saturno',  glyph: '♄', color: '#B7A98A', meaning: 'Señala estructura y disciplina.', short: 'estructura y disciplina' },
    { keys: ['uranus', 'urano'],      es: 'Urano',    glyph: '♅', color: '#6BD3F2', meaning: 'Muestra cambio y libertad.', short: 'cambio y libertad' },
    { keys: ['neptune', 'neptuno'],   es: 'Neptuno',  glyph: '♆', color: '#6B8AF2', meaning: 'Apunta a inspiración y sensibilidad.', short: 'inspiración y espiritualidad' },
    { keys: ['pluto', 'pluton'],      es: 'Plutón',   glyph: '♇', color: '#A47FD1', meaning: 'Señala transformación profunda.', short: 'transformación' },
    { keys: ['chiron', 'quiron'],     es: 'Quirón',   glyph: '⚷', color: '#A8D97F', meaning: 'Señala la herida que enseña.', short: 'herida y maestría' }
  ];
  // Angle sets: solid MC, distinct dash per set. Labels for MC/ASC sit above
  // the map, IC/DSC below, so overlapping sets stay readable. `legend` feeds
  // the one-line angle explainer in the map legend.
  var ANGLES = [
    { code: 'MC',  dash: '',        width: 1.6, labelTop: true,  name: T('medio cielo'),     micro: T('en lo alto: tu trabajo y tu imagen') },
    { code: 'IC',  dash: '6 4',     width: 1.2, labelTop: false, name: T('fondo del cielo'), micro: T('abajo: tu casa, tu familia, tus raíces') },
    { code: 'ASC', dash: '2 3',     width: 1.2, labelTop: true,  name: T('ascendente'),      micro: T('al este: cómo apareces y cómo empiezas') },
    { code: 'DSC', dash: '8 3 2 3', width: 1.2, labelTop: false, name: T('descendente'),     micro: T('al oeste: los vínculos y quién aparece') }
  ];
  var ANGLE_ALIAS = { MC: 'MC', IC: 'IC', ASC: 'ASC', AC: 'ASC', DSC: 'DSC', DC: 'DSC', DS: 'DSC' };
  var LEGEND_TITLE = T('Qué señala cada línea');

  // Map geometry (must match the section's inline SVG viewBox 0 0 720 400):
  // labels band 0-20, map area 20-380, bottom labels band 380-400.
  var MAP_W = 720, MAP_TOP = 20, MAP_BOTTOM = 380, LABEL_TOP_Y = 14, LABEL_BOTTOM_Y = 394;

  function norm(s) {
    return String(s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
  }
  function pad2(n) { return String(n).length < 2 ? '0' + n : String(n); }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function fmt(tpl, map) {
    return String(tpl || '').replace(/\{(\w+)\}/g, function (m, k) { return map[k] != null ? map[k] : ''; });
  }
  // Copy fija del bloque i18n por el diccionario (inglés): cadenas y listas de cadenas, el resto tal cual.
  function trCopy(o) {
    var out = {};
    for (var k in o) {
      if (!Object.prototype.hasOwnProperty.call(o, k)) continue;
      var v = o[k];
      out[k] = typeof v === 'string' ? T(v)
        : (Array.isArray(v) ? v.map(function (x) { return typeof x === 'string' ? T(x) : x; }) : v);
    }
    return out;
  }
  function planetFor(name) {
    var n = norm(name);
    for (var i = 0; i < PLANETS.length; i++) {
      if (PLANETS[i].keys.indexOf(n) > -1) return PLANETS[i];
    }
    return null;
  }
  function lonToX(lon) { return (Number(lon) + 180) / 360 * MAP_W; }
  function latToY(lat) { return MAP_TOP + (90 - Number(lat)) / 180 * (MAP_BOTTOM - MAP_TOP); }
  // Horizon curve ([lat, lon] points, ascending lat) -> SVG path segments.
  // A curve can wrap across the ±180° antimeridian: whenever consecutive
  // points jump more than 180° in longitude the polyline is split into a new
  // segment so no line is drawn straight across the whole map.
  function curveToPaths(points) {
    var segs = [], seg = [], prevLon = null;
    for (var i = 0; i < points.length; i++) {
      var pt = points[i];
      var lat = pt && (Array.isArray(pt) ? pt[0] : pt.lat);
      var lon = pt && (Array.isArray(pt) ? pt[1] : pt.lon);
      if (lat == null || lon == null || isNaN(Number(lat)) || isNaN(Number(lon))) continue;
      if (prevLon != null && Math.abs(Number(lon) - prevLon) > 180) {
        if (seg.length > 1) segs.push(seg);
        seg = [];
      }
      seg.push([lonToX(lon), latToY(lat)]);
      prevLon = Number(lon);
    }
    if (seg.length > 1) segs.push(seg);
    return segs.map(function (s) {
      return 'M' + s.map(function (q) { return q[0].toFixed(1) + ' ' + q[1].toFixed(1); }).join('L');
    });
  }
  function fmtLon(lon) {
    var v = Number(lon);
    if (isEn()) return Math.abs(v).toFixed(1) + '° ' + (v >= 0 ? 'E' : 'W'); // decimal point, W for West
    var hemi = v >= 0 ? 'E' : 'O';
    return Math.abs(v).toFixed(1).replace('.', ',') + '° ' + hemi;
  }

  function track(name) {
    // Intentionally carries NO birth data.
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: name });
    } catch (e) {}
    try { document.dispatchEvent(new CustomEvent('aw:track', { detail: { name: name } })); } catch (e) {}
    // Completar la calculadora también viaja al píxel de Meta (custom pixel) como
    // evento personalizado: es el único paso con volumen para que aprenda.
    if (name === 'astromapa_complete') {
      try { if (typeof window.awPublish === 'function') window.awPublish('calculadora_completada', { calc: 'astro' }); } catch (e) {}
    }
  }

  /* fetch + JSON with a hard timeout: the lines call used to have none,
     which left the submit button busy forever on a stalled connection. */
  function fetchJson(url, opts, timeoutMs, ctrl) {
    var timer = null;
    if (!ctrl) { try { ctrl = new AbortController(); } catch (e) { ctrl = null; } }
    if (ctrl) {
      opts.signal = ctrl.signal;
      timer = setTimeout(function () { try { ctrl.abort(); } catch (e) {} }, timeoutMs || 25000);
    }
    function clear() { if (timer) clearTimeout(timer); }
    return fetch(url, opts).then(function (res) {
      clear();
      if (!res.ok) { var err = new Error('http'); err.status = res.status; throw err; }
      return res.json();
    }, function (e) { clear(); throw e; });
  }

  /* Pure: which text belongs on the scan ribbon for a given summary-flow
     state. The ribbon derives from the PROMISE state, never from a clock:
     no work gerund may remain on screen after the fetch settles. */
  function scanTextFor(state, t, slow) {
    if (state === 'pending') return slow ? (t.scanSlow || t.scanLine || '') : (t.scanLine || '');
    if (state === 'done') return t.scanDone || '';
    return ''; // error/idle: the ribbon never claims anything
  }

  /* Pure generation-token state machine for the summary fetch: a stale
     response (an earlier calculate still in flight after a recalculate)
     must never paint another chart's data, and nothing paints twice.
     Kept outside initAstro so Node tests can drive it directly. */
  function createSummaryFlow() {
    var gen = 0, state = 'idle';
    return {
      start: function () { gen += 1; state = 'pending'; return gen; },
      resolve: function (g) {
        if (g !== gen || state !== 'pending') return false;
        state = 'done'; return true;
      },
      fail: function (g) {
        if (g !== gen || state !== 'pending') return false;
        state = 'error'; return true;
      },
      retry: function () {
        if (state !== 'error') return null;
        state = 'pending'; return gen;
      },
      state: function () { return state; },
      gen: function () { return gen; }
    };
  }

  function initAstro(root) {
    var ORDER = ['intro', 'date', 'time', 'place', 'calc', 'gate', 'result'];
    var STEPNUM = { date: 1, time: 2, place: 3 };
    var current = 'intro';

    var apiUrl = root.getAttribute('data-api-url');
    var placesProxy = (root.getAttribute('data-places-proxy') || '').replace(/\/+$/, '');
    // sesión de Google y memoria de respuestas (aw-places.js, 2026-10-01)
    var places = (typeof AWPlaces !== 'undefined' && placesProxy) ? AWPlaces.client(placesProxy) : null;
    // El idioma de la petición a la API (ES/EN), no el de la página: con el nombre lang tapaba la función lang() de
    // arriba en todo initAstro, y lang() lanzaba un TypeError que dejaba la calculadora sin arrancar (2026-10-22).
    var apiLang = root.getAttribute('data-lang') || 'ES';

    // ── suspense machinery state (all of it inert without the attribute) ──
    var suspense = root.hasAttribute('data-aw-astro-suspense');
    var SCAN_FLOOR = parseInt(root.getAttribute('data-astro-scan-floor'), 10) || 0;
    var RM = false, LITE = false, SLOWCONN = false;
    try { RM = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}
    try {
      LITE = (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4) ||
             (navigator.deviceMemory && navigator.deviceMemory <= 2);
    } catch (e) {}
    try {
      var conn = navigator.connection;
      SLOWCONN = !!(conn && (conn.saveData || /(^|\b)(slow-2g|2g|3g)$/.test(conn.effectiveType || '')));
    } catch (e) {}
    var submitAt = 0;
    var lastPickedName = '';

    var t = {};
    try { t = JSON.parse(root.querySelector('[data-aw-astro-i18n]').textContent); } catch (e) { t = {}; }
    // En inglés, la copy fija del bloque i18n (en español en la sección: las plantillas con llaves solo pueden vivir
    // ahí) pasa por el diccionario. Los ajustes de la instancia inglesa ya llegan en inglés y salen tal cual.
    if (lang() !== 'es') t = trCopy(t); // inglés y francés: el diccionario de la página

    var screens = {};
    Array.prototype.forEach.call(root.querySelectorAll('[data-aw-astro-screen]'), function (el) {
      screens[el.getAttribute('data-aw-astro-screen')] = el;
    });
    var chrome = root.querySelector('[data-aw-astro-chrome]');
    var stepCount = root.querySelector('[data-aw-astro-stepcount]');
    var progress = root.querySelector('[data-aw-astro-progress]');
    var progressFill = root.querySelector('[data-aw-astro-progress-fill]');
    var errorBox = root.querySelector('[data-aw-astro-error]');
    var submitBtn = root.querySelector('[data-aw-astro-submit]');

    var dayEl = root.querySelector('[data-aw-astro-day]');
    var monthEl = root.querySelector('[data-aw-astro-month]');
    var yearEl = root.querySelector('[data-aw-astro-year]');
    var hourEl = root.querySelector('[data-aw-astro-hour]');
    var minEl = root.querySelector('[data-aw-astro-min]');
    var placeEl = root.querySelector('[data-aw-astro-place]');
    var suggestEl = root.querySelector('[data-aw-astro-suggest]');
    var linesEl = root.querySelector('[data-aw-astro-lines]');
    var listEl = root.querySelector('[data-aw-astro-list]');

    // ── Places autocomplete state (lat/lon via the Worker proxy; NO API key here) ──
    var placeLat = null, placeLon = null;
    var placePicked = false;
    var acSeen = false;
    var acFailed = false;
    var acItems = [];
    var acLast = [];   // survives clearSuggest: the exact-match valve reads it
    var acActive = -1; // keyboard-highlighted option index
    var acTimer = null;

    var booted = false;
    var started = false;
    // Per-step funnel (structure only, no birth data): one-shot event the first
    // time each wizard step is reached, so abandonment BY STEP is computable
    // exactly in GA4 (pre-registered diagnostic 2026-08-29, GTM ^(astro_) covers it).
    var stepSeen = {};
    function go(s, opts) {
      current = s;
      if (!started && STEPNUM[s]) { started = true; track('astromapa_start'); } // first real step
      if (STEPNUM[s] && !stepSeen[s]) { stepSeen[s] = true; track('astro_step_' + s); }
      clearSuggest();
      ORDER.forEach(function (k) { if (screens[k]) screens[k].classList.toggle('is-active', k === s); });
      // La capa de respuestas (SEO) se apaga desde el teatro en adelante. Marca de estado
      // en la raíz, para que el CSS decida sin que el JS sepa nada del bloque hermano.
      root.setAttribute('data-screen', s);
      if (chrome) chrome.classList.toggle('can-back', s !== 'intro' && s !== 'result' && s !== 'calc' && s !== 'gate');
      if (STEPNUM[s]) {
        stepCount.textContent = fmt(T('paso {n} de 3'), { n: STEPNUM[s] });
        progress.classList.add('show');
        progressFill.style.width = (STEPNUM[s] / 3 * 100) + '%';
      } else if (s === 'calc') {
        stepCount.textContent = '';
        progress.classList.add('show');
        progressFill.style.width = '100%';
      } else {
        stepCount.textContent = '';
        progress.classList.remove('show');
      }
      if (screens[s]) screens[s].scrollTop = 0;
      if (booted) { try { root.scrollIntoView({ block: 'start', behavior: 'smooth' }); } catch (e) {} }
      booted = true;
      if (hist) hist.at(s, !!(opts && opts.fromPop));
    }

    function back() {
      if (hist && hist.canBack()) { hist.back(); return; }
      var i = ORDER.indexOf(current);
      if (i > 0) go(ORDER[i - 1]);
    }

    // Historial por pantalla (14-oct, aw-step-history.js): «atrás» del móvil vuelve al paso anterior en vez
    // de cerrar el navegador de Instagram. Salir del teatro o de la puerta descarta el resultado en vuelo (live).
    var calcSeq = 0, calcLeft = false, runTh = null, runCtrl = null;
    function live(id) { return id === calcSeq && !calcLeft; }
    // Salir del teatro o de la puerta cancela el cálculo en curso: teatro parado, petición abortada, botón libre.
    function cancelRun() {
      calcLeft = true;
      if (runTh) { try { runTh.clear(); } catch (e) {} runTh = null; }
      if (runCtrl) { try { runCtrl.abort(); } catch (e) {} runCtrl = null; }
      setBusy(false);
    }
    var hist = window.AWStepHistory ? window.AWStepHistory.attach({ key: 'awAstro', transient: ['calc', 'gate'], landing: 'intro', onPop: function (s) {
      if (s === current) return;
      if (current === 'calc' || current === 'gate') cancelRun();
      go(s, { fromPop: true });
    } }) : null;

    function showError(msg) { if (errorBox) { errorBox.textContent = msg; errorBox.classList.add('is-show'); } }
    function clearError() { if (errorBox) { errorBox.textContent = ''; errorBox.classList.remove('is-show'); } }
    function setBusy(b) {
      if (!submitBtn) return;
      submitBtn.classList.toggle('is-busy', b);
      submitBtn.textContent = b ? (t.busyLabel || T('Calculando…')) : (submitBtn.getAttribute('data-label') || submitBtn.textContent);
    }

    // ── email gate (spec 2026-09-08), solo con data-aw-gate en el root ──
    // Astro manda lo que el resultado ya pintó detrás de la puerta (11-sep, spec
    // §10.6): el summary corre en paralelo y el teaser lo pinta en cuanto llega, así
    // que el gate lee gateFacts al enviar. Sin pintado todavía, sale solo calc_gate.
    var gateFlow = root.getAttribute('data-aw-gate');
    var gateI18n = {};
    try { gateI18n = JSON.parse(root.querySelector('[data-aw-gate-i18n]').textContent); } catch (e) { gateI18n = {}; }
    function gateOn() { return !!(gateFlow && screens.gate && window.AWGate); }
    function theaterOn() { return !!(gateFlow && screens.calc && window.AWTheater); }
    function showConfirmLine() { var el = root.querySelector('[data-aw-gate-confirm]'); if (el) el.hidden = false; }
    function storageOrNull() { try { return window.localStorage; } catch (e) { return null; } }

    // ── validation ────────────────────────────────────────────
    function validDate() {
      var d = parseInt(dayEl.value, 10), y = parseInt(yearEl.value, 10);
      return d >= 1 && d <= 31 && y >= 1900 && y <= (new Date().getFullYear() + 1);
    }
    function validTime() {
      var h = parseInt(hourEl.value, 10), m = parseInt(minEl.value, 10);
      return h >= 0 && h <= 23 && m >= 0 && m <= 59;
    }
    function validPlace() { return placeEl && placeEl.value.trim().length >= 2; }

    function humanDate() {
      var monthName = monthEl.options[monthEl.selectedIndex].text;
      var f = own(DATE_FMT, lang()), dd = parseInt(dayEl.value, 10);
      if (f) return fmt(f, { m: monthName, d: lang() === 'fr' && dd === 1 ? '1er' : dd, y: yearEl.value }); // March 14, 1990 / 14 mars 1990
      return dayEl.value + ' de ' + monthName + ' de ' + yearEl.value;
    }

    // ── Places autocomplete (via Cloudflare Worker proxy) ──────
    function clearSuggest() {
      acItems = [];
      acActive = -1;
      if (suggestEl) { suggestEl.innerHTML = ''; suggestEl.hidden = true; }
      if (placeEl) {
        placeEl.setAttribute('aria-expanded', 'false');
        placeEl.removeAttribute('aria-activedescendant');
      }
    }
    // Options are plain li[role=option] content (a button inside an option
    // is invalid ARIA and used to trap keyboard users); the class name is
    // kept so the existing row styles still apply.
    function renderSuggest(items) {
      acItems = items || [];
      if (acItems.length) acLast = acItems;
      acActive = -1;
      if (!suggestEl) return;
      if (!acItems.length) { clearSuggest(); return; }
      suggestEl.innerHTML = acItems.map(function (s, i) {
        return '<li class="aw-astro__suggest-item" role="option" id="' + esc(placeEl.id + '-opt-' + i) + '" data-i="' + i + '">' +
          '<span class="aw-astro__suggest-btn">' +
          '<span class="aw-astro__c-name">' + esc(s.name) + '</span>' +
          (s.region ? '<span class="aw-astro__c-region">' + esc(s.region) + '</span>' : '') +
          '</span></li>';
      }).join('');
      suggestEl.hidden = false;
      if (placeEl) placeEl.setAttribute('aria-expanded', 'true');
    }
    function moveActive(delta) {
      if (!acItems.length || suggestEl.hidden) return;
      acActive = (acActive + delta + acItems.length) % acItems.length;
      Array.prototype.forEach.call(suggestEl.children, function (li, i) {
        li.classList.toggle('is-active', i === acActive);
      });
      placeEl.setAttribute('aria-activedescendant', placeEl.id + '-opt-' + acActive);
    }
    function fetchAutocomplete(q) {
      if (!places) return;
      places.suggest(q)
        .then(function (d) {
          acFailed = false;
          var arr = (d && d.predictions) || [];
          var items = arr.slice(0, 5).map(function (p) {
            return {
              text: p.description || p.main_text || '',
              name: p.main_text || p.description || '',
              region: p.secondary_text || '',
              id: p.place_id || ''
            };
          }).filter(function (x) { return x.text; });
          if (items.length) acSeen = true;
          if (placeEl.value.trim().length >= 3) renderSuggest(items); // ignore stale
        })
        .catch(function () { acFailed = true; renderSuggest([]); });
    }
    function pickSuggestion(item) {
      if (!item) return;
      placeEl.value = item.text;
      placePicked = true;
      lastPickedName = item.name || '';
      clearSuggest();
      clearError();
      placeLat = null; placeLon = null;
      if (!item.id || !places) return;
      places.details(item.id)
        .then(function (d) {
          if (d && d.lat != null && d.lng != null) { placeLat = Number(d.lat); placeLon = Number(d.lng); }
        })
        .catch(function () { placeLat = null; placeLon = null; });
    }

    // ── the call ──────────────────────────────────────────────
    var announceEl = root.querySelector('[data-aw-astro-status]');
    function announce(msg) { if (announceEl && msg) announceEl.textContent = msg; }

    function calculate() {
      clearError();
      if (!validDate() || !validTime() || !validPlace()) { showError(t.errInvalid || T('Revisa los datos.')); return; }
      if (!placePicked && acSeen && !acFailed) {
        // Escape valve: typed text that exactly matches a suggestion counts
        // as a pick (keyboard users must never dead-end on the mouse list).
        var typed = norm(placeEl.value), matched = false;
        for (var mi = 0; mi < acLast.length; mi++) {
          if (norm(acLast[mi].text) === typed || norm(acLast[mi].name) === typed) { matched = true; break; }
        }
        if (matched) { placePicked = true; }
        else { showError(t.placePick || T('Elige tu ciudad de la lista.')); return; }
      }

      var payload = {
        date: yearEl.value + '-' + pad2(parseInt(monthEl.value, 10)) + '-' + pad2(parseInt(dayEl.value, 10)),
        time: pad2(parseInt(hourEl.value, 10)) + ':' + pad2(parseInt(minEl.value, 10)),
        place: placeEl.value.trim(),
        lang: apiLang
      };
      if (placeLat != null && placeLon != null) { payload.lat = placeLat; payload.lon = placeLon; }
      var placeText = payload.place;
      var dateText = humanDate();
      var timeText = payload.time;
      if (isEn()) { var h24 = parseInt(hourEl.value, 10); timeText = (h24 % 12 || 12) + ':' + pad2(parseInt(minEl.value, 10)) + ' ' + (h24 < 12 ? 'am' : 'pm'); } // 8:30 pm
      else if (lang() === 'fr') timeText = parseInt(hourEl.value, 10) + '\u00a0h\u00a0' + pad2(parseInt(minEl.value, 10)); // 14 h 30

      var reqId = ++calcSeq; calcLeft = false;
      setBusy(true);
      announce(t.srCalculating || '');
      submitAt = Date.now();
      if (suspense && SCAN_FLOOR > 0 && SLOWCONN) track('astro_theater_bypass');
      var th = null;
      var floorP = Promise.resolve();
      if (theaterOn()) {
        go('calc');
        track('astromapa_theater_start');
        th = window.AWTheater.start(screens.calc, { lines: gateI18n.theater || [], floor: window.AWTheater.GATE_FLOOR, spread: true, slowText: gateI18n.slowText || '' });
        runTh = th;
        floorP = th.floorP;
      }
      // Lines and summary fire IN PARALLEL from the submit: the
      // summary (~2,4 s p50, semaphore of 1 server-side) starts its wait now
      // instead of after the map renders. The map NEVER waits on it.
      runCtrl = null;
      try { runCtrl = new AbortController(); } catch (e) {}
      var linesReq = fetchJson(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        mode: 'cors',
        credentials: 'omit'
      }, 25000, runCtrl);
      startSummary(payload);
      // Reveal (spec 2026-09-08): idéntico al de antes, envuelto para poder
      // intercalar teatro y gate. Sin bandera se ejecuta en el mismo tick que hoy.
      /* Pintar el mapa es independiente de la coreografia del reveal, asi que se
         puede adelantar: con la puerta encima queremos que detras se vea SU
         mapa con SUS lineas, no el planisferio vacio. Idempotente, porque lo llaman
         los dos caminos (con puerta y sin ella). El dibujo progresivo, la vista y la
         cinta de escaneo siguen en el reveal, que es donde tienen sentido. */
      var mapaPintado = false;
      function pintarAstro(data) {
        if (mapaPintado) return;
        mapaPintado = true;
        renderResult(data, placeText, dateText, timeText);
      }

      function revealAstro(data) {
        setBusy(false);
        pintarAstro(data);
        var jump = root.querySelector('[data-aw-astro-jump]');
        if (jump) jump.removeAttribute('hidden');
        go('result');
        // Suspense sequence starts in the same task as the screen swap so
        // the staging classes apply before the first visible paint.
        runMapDraw();
        initMapView();
        startScanRibbon();
        announce(t.srReady || '');
        track('astro_map_ready');
        track('astromapa_complete'); // no birth data
        // Resultado persistente (3-sep): 9 de 79 volvían de la landing a un
        // formulario vacío. Cache en el navegador del usuario, nunca en analytics.
        try {
          sessionStorage.setItem('aw_astro_last', JSON.stringify({
            data: data, placeText: placeText, dateText: dateText, timeText: timeText,
            payload: payload, ts: Date.now()
          }));
        } catch (e) {}
        showTeaserSkeleton();
      }
      linesReq.then(function (data) {
        return floorP.then(function () { return data; });
      }).then(function (data) {
        if (th) th.clear();
        if (!live(reqId)) { if (reqId === calcSeq) setBusy(false); return; }
        // Desde «Abrir en Chrome» (shareEscape) tampoco hay puerta: ya la pasó en el navegador de la app.
        if (!gateOn() || shareEscape || !window.AWGate.shouldShow({ storage: storageOrNull(), search: window.location.search, known: root.hasAttribute('data-aw-gate-known') })) {
          revealAstro(data);
          return;
        }
        pintarAstro(data);
        go('gate');
        // Dura para todo el tráfico (founder 2026-09-21): sin «Verlo sin dejar mi email»,
        // venga de un anuncio o no. Quien tiene sesión iniciada no ve la puerta (shouldShow).
        // verifyUrl (18-sep): la API verifica el buzón y elige la vía; la línea de
        // confirmación la pone showRoute cuando llega su respuesta, tras el reveal.
        var gateCfg = {
          flow: 'astro', hard: true,
          listId: root.getAttribute('data-aw-gate-list') || '',
          companyId: root.getAttribute('data-aw-gate-company') || '',
          verifyUrl: root.getAttribute('data-aw-gate-verify') || '',
          props: function () { return gateFacts; },
          storage: storageOrNull(),
          track: track,
          onLead: window.awSendLead,
          errInvalid: gateI18n.errInvalid
        };
        return window.AWGate.run(screens.gate, gateCfg).then(function (out) {
          if (!live(reqId)) return;
          if (out && out.captured) {
            if (out.route && typeof window.AWGate.showRoute === 'function') window.AWGate.showRoute(root, out.route, gateCfg);
            else showConfirmLine();
          }
          revealAstro(data);
        });
      }).catch(function (err) {
        if (th) th.clear();
        if (!live(reqId)) { if (reqId === calcSeq) setBusy(false); return; }
        setBusy(false);
        if (current === 'calc' || current === 'gate') go('place');
        showError(errorMessage(err && err.status));
      });
    }

    // ── teaser handoff (flag-gated: [data-aw-astro-interp] only exists
    // when section.settings.teaser_enabled is on) ──────────────────
    // Resets the interpretation zone to its flag-off look. Scoped to
    // interpEl so it never touches the birth-place search on screen "place",
    // which shares the same .aw-astro__search-wrap class.
    function clearTeaserZone(interpEl) {
      interpEl.setAttribute('hidden', '');
      var unitsEl = interpEl.querySelector('[data-aw-astro-units]');
      if (unitsEl) unitsEl.innerHTML = '';
      var resultsEl = interpEl.querySelector('[data-aw-astro-city-results]');
      if (resultsEl) resultsEl.innerHTML = '';
      var scoreEl = interpEl.querySelector('[data-aw-astro-stat="birth_score"]');
      if (scoreEl) scoreEl.textContent = '';
      var barEl = interpEl.querySelector('[data-aw-astro-bar="birth"]');
      if (barEl) barEl.style.width = '0%';
      var linesEl2 = interpEl.querySelector('[data-aw-astro-stat="near_lines"]');
      if (linesEl2) linesEl2.textContent = '';
      var capLeftEl = interpEl.querySelector('[data-aw-astro-cap-left]');
      if (capLeftEl) capLeftEl.setAttribute('hidden', '');
      var topEl = interpEl.querySelector('[data-aw-astro-stat="top_city"]');
      if (topEl) topEl.innerHTML = '';
      var capEl = interpEl.querySelector('[data-aw-astro-cap]');
      if (capEl) capEl.setAttribute('hidden', '');
      var cityInputEl = interpEl.querySelector('[data-aw-astro-city-input]');
      if (cityInputEl) { cityInputEl.value = ''; cityInputEl.disabled = false; }
      var searchWrapEl = interpEl.querySelector('.aw-astro__search-wrap');
      if (searchWrapEl) searchWrapEl.hidden = false;
      // Recibo del puente (5-sep): un recálculo no hereda la lista, el eco ni
      // la línea de área del mapa anterior (el puente vive fuera de interpEl).
      try {
        if (window.AWAstroTeaser && typeof window.AWAstroTeaser.resetSession === 'function') window.AWAstroTeaser.resetSession(root, t);
      } catch (e) {}
    }

    // ── summary flow (interpretation zone) ─────────────────────
    // Generation token via createSummaryFlow(): a recalculate invalidates
    // any summary still in flight, so stale data never paints. The zone is
    // promised by the jump pill, so pending shows a skeleton and failure
    // shows a visible error with retry — never today's silent hide.
    var summaryFlow = createSummaryFlow();
    var summaryCtx = null; // { payload, endpoint, libUrl } for the current gen
    // 7-sep: vivia entre las variables del flujo natal, que ya no existe. Lo usan
    // startSummary (lo baja) y applySummary (lo sube), asi que su sitio es aqui.
    var summaryPainted = false;
    // «Comparte tu mapa» (24-sep): la tira necesita las líneas (/astrocartography-lines) y la
    // ciudad más alta (/astrocartography-basic). Llegan en paralelo y en cualquier orden:
    // se monta con la segunda. shareEscape es la marca de «Abrir en Chrome» (compartir=<diseño>).
    var shareP = null, shareEscape = '', shareLines = null, shareSummary = null, shareBirth = null, shareMounted = null;
    function mountShare() {
      if (!shareLines || !shareSummary || shareMounted === shareSummary) return;
      if (!window.AWShare || typeof window.AWShare.astro !== 'function') return;
      shareMounted = shareSummary;
      var b = shareBirth || {};
      try {
        shareP = window.AWShare.astro(root, shareSummary, shareLines, { date: b.date, time: b.time, place: b.place, lat: b.lat, lon: b.lon }, track);
      } catch (e) { shareP = null; }
      // Desde «Abrir en Chrome»: la hoja se abre sola en el diseño que tenía.
      if (shareEscape && shareP && shareP.then) {
        var esc = shareEscape;
        shareEscape = '';
        shareP.then(function (ctl) { if (ctl) ctl.open(esc); }).catch(function () {});
      }
    }
    // Lo que el último pintado enseña de la carta, para el gate de email (11-sep).
    // Lo escribe el pintado, lo baja startSummary y el gate lo lee al enviar.
    var gateFacts = null;
    // Recuperación del motor del teaser (6-sep). Un <script defer> que no
    // descarga NO lanza error de JS: la página se decapitaba en silencio (mapa
    // y recibo arriba, promesa en pie, nada detrás). Un solo dueño del asset,
    // applySummary: espera acotada, hasta 3 reinyecciones espaciadas del mismo
    // src que ya sirvió el tema, y rendición honesta con reintento si no llega.
    var TEASER_MAX_TRIES = 3;
    var pendingPaint = null, teaserTries = 0, teaserLoading = false, teaserDead = false,
      teaserWaiting = false, teaserMissingSent = false, teaserRecoveredSent = false;

    function interpParts() {
      var interpEl = root.querySelector('[data-aw-astro-interp]');
      if (!interpEl) return null;
      return {
        el: interpEl,
        status: interpEl.querySelector('[data-aw-astro-interp-status]'),
        errBox: interpEl.querySelector('[data-aw-astro-interp-error]'),
        errMsg: interpEl.querySelector('[data-aw-astro-interp-error-msg]'),
        retryBtn: interpEl.querySelector('[data-aw-astro-summary-retry]')
      };
    }
    function hidePendingStates(p) {
      if (p.status) p.status.setAttribute('hidden', '');
      if (p.errBox) p.errBox.setAttribute('hidden', '');
    }
    function teaserReady() {
      return !!(window.AWAstroTeaser && typeof window.AWAstroTeaser.render === 'function');
    }
    // La promesa ("Hasta aquí, la posición de tus líneas. Lo que sigue es su
    // significado.") no puede quedarse en pie si detrás no viene nada. El nodo
    // es opcional: lo gobierna el setting data_close de la sección.
    function showPromise(on) {
      var el = root.querySelector('.aw-astro__data-close');
      if (!el) return;
      if (on) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
    }
    // Andamiaje que SOLO puede llenar el teaser: las tres etiquetas de stat con
    // su cifra vacía, las notas que enmarcan esas cifras y la tarjeta de ciudad
    // entera (su input lo cablea AWAstroTeaser.initCitySearch, que en un estado
    // degradado no corre: quedaría un control muerto y la frase del tope
    // afirmando una capacidad que no existe). Revelar la zona sin retirarlo es
    // prometer contenido que no está.
    var SCAFFOLD = ['[data-aw-astro-stats]', '[data-aw-astro-citycard]', '.aw-astro__score-note', '.aw-astro__score-method', '.aw-astro__distance-note'];
    function showScaffold(on) {
      var p = interpParts();
      if (!p) return;
      for (var i = 0; i < SCAFFOLD.length; i++) {
        var el = p.el.querySelector(SCAFFOLD[i]);
        if (!el) continue;
        if (on) el.removeAttribute('hidden');
        else el.setAttribute('hidden', '');
      }
    }
    function showInterpLoading() {
      var p = interpParts();
      if (!p) return;
      if (p.errBox) p.errBox.setAttribute('hidden', '');
      if (p.status) {
        p.status.textContent = t.interpLoading || T('Estamos cargando esta sección. Tu mapa ya está completo aquí arriba.');
        p.status.removeAttribute('hidden');
      }
      p.el.removeAttribute('hidden');
    }
    // Rendición honesta: la zona queda VISIBLE con lo que sí es verdad y con un
    // reintento real. NO llama a hideScanRibbon (la cinta ya cerró con un
    // mensaje cierto: las ciudades sí se puntuaron).
    // (paintLate reconstruye desde la receta de sesión que solo escribe el
    // teaser). Dos causas, dos frases: sin motor (broken falsy) nada llegó a
    // pintarse; con motor que lanzó (broken) hay cifras y unidades REALES en
    // pantalla, así que ese camino limpia primero y no dice "no se ha cargado".
    function showTeaserUnavailable(broken) {
      var p = interpParts();
      if (!p) return;
      if (broken) clearTeaserZone(p.el); // oculta la zona: se revela dos líneas más abajo
      if (p.status) p.status.setAttribute('hidden', '');
      showScaffold(false); // sin datos detrás, el andamiaje del teaser no se enseña
      p.el.removeAttribute('hidden');
      if (p.errMsg) {
        p.errMsg.textContent = broken
          ? (t.interpBroken || T('Esta sección no se ha podido completar. Tu mapa y la posición de tus líneas siguen completos aquí arriba.'))
          : (t.interpUnavailable || T('Esta sección no se ha cargado en tu navegador. Tu mapa y la posición de tus líneas siguen completos aquí arriba.'));
      }
      if (p.retryBtn) {
        p.retryBtn.textContent = t.interpRetry || T('Volver a cargar esta sección');
        wireRetry(p);
      }
      if (p.errBox) p.errBox.removeAttribute('hidden');
      showPromise(false);
      track('astro_teaser_unavailable');
    }
    function reinjectTeaser() {
      if (teaserLoading || teaserDead || teaserTries >= TEASER_MAX_TRIES) return;
      teaserTries += 1;
      teaserLoading = true;
      // Único punto por el que pasan las DOS rutas de detección (el esqueleto,
      // ~2,4 s antes del resumen, y el pintado). Emitir el evento aquí es lo que
      // hace computable la tasa de curación: si solo se emitiera al esperar,
      // las decapitaciones que el esqueleto cura solas quedarían invisibles y el
      // ratio mediría únicamente los fallos tardíos.
      if (!teaserMissingSent) { teaserMissingSent = true; track('astro_teaser_missing'); }
      // La URL versionada sale del DOM servido, nunca se construye a mano.
      // Reejecutar aw-astromapa-teaser.js es seguro porque ese archivo no tiene
      // NI UNA sentencia ejecutable de nivel superior: solo define funciones y
      // asigna window.AWAstroTeaser. Si alguien le añade un efecto de carga,
      // esta reinyección lo duplicará.
      var tag = document.querySelector('script[src*="aw-astromapa-teaser"]:not([data-aw-astro-reinject])');
      if (!tag) { teaserDead = true; teaserLoading = false; return; }
      var s = document.createElement('script');
      s.src = tag.getAttribute('src');
      s.setAttribute('data-aw-astro-reinject', '1');
      s.onload = function () { teaserLoading = false; };
      // Un 404 AHORA no es un 404 dentro de un segundo: la causa medida es la
      // ventana de redespliegue del tema, que dura segundos. Por eso un error
      // libera el turno en vez de matar la espera, y solo se declara muerto
      // cuando se agotan los intentos.
      s.onerror = function () {
        teaserLoading = false;
        if (teaserTries >= TEASER_MAX_TRIES) teaserDead = true;
      };
      document.head.appendChild(s);
    }
    // Espera acotada (50 × 100 ms = 5 s, el mismo precedente que la ruta de
    // restauración). teaserDead corta antes: un 404 sobre una URL con ?v= es
    // permanente. Una sola cadena viva, sin handles de temporizador que limpiar.
    function whenTeaserReady(g, onReady, onGiveUp) {
      if (teaserReady()) { onReady(); return; }
      if (teaserWaiting) return;
      teaserWaiting = true;
      var tries = 0, sinceTry = 0;
      (function tick() {
        if (summaryFlow.gen() !== g) { teaserWaiting = false; return; } // un recálculo mata la cadena
        if (teaserReady()) { teaserWaiting = false; onReady(); return; } // la curación la firma el pintado
        if (teaserDead || tries >= 50) { teaserWaiting = false; onGiveUp(); return; }
        // Hasta TEASER_MAX_TRIES intentos espaciados ~1,5 s dentro de los 5 s:
        // cubre la ventana corta de redespliegue sin retener a nadie mirando un
        // cargando. Si se agota, manda la rendición honesta con su reintento.
        if (!teaserLoading && teaserTries < TEASER_MAX_TRIES && sinceTry <= 0) {
          reinjectTeaser();
          sinceTry = 15;
        }
        sinceTry -= 1;
        tries += 1;
        setTimeout(tick, 100);
      })();
    }

    function startSummary(birthPayload) {
      var p = interpParts();
      if (!p) return; // flag off: markup doesn't exist, stay inert
      var endpoint = p.el.getAttribute('data-aw-astro-basic-endpoint');
      var libUrl = p.el.getAttribute('data-aw-astro-lib');
      if (!endpoint || !libUrl) return;
      summaryCtx = { payload: birthPayload, endpoint: endpoint, libUrl: libUrl };
      summaryPainted = false;
      shareSummary = null; shareBirth = birthPayload;
      var oldStrip = root.querySelector('[data-aw-share]');
      if (oldStrip) oldStrip.hidden = true;
      gateFacts = null;
      // Un cálculo nuevo no hereda la promesa retirada ni el pintado pendiente
      // de otra carta (el guardián de generación ya lo bloquea, esto lo explicita).
      pendingPaint = null;
      showPromise(true);
      fireSummaryFetch(summaryFlow.start());
    }
    function fireSummaryFetch(gen) {
      var summaryFetch = fetchJson(summaryCtx.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(summaryCtx.payload),
        mode: 'cors',
        credentials: 'omit'
      }, 25000);
      var libFetch = fetchJson(summaryCtx.libUrl, { credentials: 'omit' }, 25000);
      Promise.all([summaryFetch, libFetch]).then(function (results) {
        if (!summaryFlow.resolve(gen)) return; // stale or already painted
        applySummary(results[0], results[1]);
      }).catch(function () {
        if (!summaryFlow.fail(gen)) return;
        showSummaryError();
      });
    }
    function applySummary(summary, lib) {
      // Optional floor: with the ribbon showing, hold the REVEAL (never the
      // map) until the floor elapses. The held state shows scanDone — no
      // work gerund survives the resolve (hard rule 1). Floor default is 0.
      var remaining = 0;
      if (suspense && SCAN_FLOOR > 0 && !SLOWCONN && !RM && submitAt) {
        remaining = Math.max(0, SCAN_FLOOR - (Date.now() - submitAt));
      }
      endScanRibbon();
      var g = summaryFlow.gen();
      var paint = function () {
        var p = interpParts();
        if (!p) return;
        // La espera del motor hace posible que un recálculo esté en curso cuando
        // el teaser aterrice: sin este guardián se repintarían datos de otra carta.
        if (summaryFlow.gen() !== g) return;
        if (!teaserReady()) {
          pendingPaint = paint;
          showInterpLoading();
          whenTeaserReady(g, paint, showTeaserUnavailable);
          // El resumen ya está pagado: se guarda con SU clave para que una
          // recarga lo repinte sin volver a llamar a la API.
          augmentCache({ summary: summary, lib: lib }, cacheKey(summaryCtx.payload));
          return;
        }
        hidePendingStates(p);
        showPromise(true);
        showScaffold(true); // un estado degradado anterior lo había retirado
        t.cityName = lastPickedName || String(summaryCtx.payload.place || '').split(',')[0].trim();
        // Un throw aquí dejaba la página en esqueleto PARA SIEMPRE (resolve ya
        // puso el estado en 'done', así que showSummaryError no corre nunca).
        try {
          window.AWAstroTeaser.render(summary, lib, root, t);
        } catch (e) {
          track('astro_js_error'); // no perder la señal G10 que el catch se traga
          // El botón del estado degradado tiene que hacer algo: aquí el motor SÍ
          // está (solo lanzó), así que summaryFlow.retry() devolvería null y el
          // clic sería mudo. Dejando el repintado disponible, el reintento vuelve
          // a intentarlo con el resumen que ya está en memoria (cero POST).
          pendingPaint = paint;
          showTeaserUnavailable(true);
          return;
        }
        summaryPainted = true;
        shareSummary = summary;
        mountShare();
        // La curación la firma el pintado, no el tick de la espera: así cuentan
        // también las que cura el esqueleto antes de que llegue el resumen.
        if (teaserMissingSent && !teaserRecoveredSent) { teaserRecoveredSent = true; track('astro_teaser_recovered'); }
        // Gate de email (11-sep): lo que este pintado enseña de la carta, con la propia
        // selección del teaser. Un fallo aquí no toca el pintado, solo deja calc_gate.
        try {
          gateFacts = typeof window.AWAstroTeaser.gateProps === 'function' ? window.AWAstroTeaser.gateProps(summary, lib, root, t) : null;
        } catch (e) { gateFacts = null; }
        if (typeof window.AWAstroTeaser.initCitySearch === 'function') {
          window.AWAstroTeaser.initCitySearch(root, summaryCtx.payload, summaryCtx.endpoint, t);
        }
        // Completar la caché del resultado con el teaser ya pintado.
        augmentCache({ summary: summary, lib: lib, cityName: t.cityName });
      };
      if (remaining > 0) setTimeout(paint, remaining);
      else paint();
    }
    // Called when the result screen opens: if the summary is still in
    // flight, the zone appears in skeleton state so the jump pill always
    // lands somewhere real (the old flow left a dead click for ~2,5 s).
    function showTeaserSkeleton() {
      var p = interpParts();
      if (!p) return;
      var st = summaryFlow.state();
      if (st !== 'pending') return; // done → render already handled it; error → error box owns it
      // El esqueleto se pinta siempre (markup servido + t.skeletonStatus), así la
      // pastilla de salto nunca apunta a un ancla muerta. Y la recuperación
      // arranca aquí, ~2,4 s antes de que aterrice el resumen.
      if (!teaserReady()) reinjectTeaser();
      clearTeaserZone(p.el);
      p.el.removeAttribute('hidden');
      if (p.status) {
        p.status.textContent = t.skeletonStatus || '';
        p.status.removeAttribute('hidden');
      }
    }
    function afterRetryReady() {
      if (pendingPaint) { pendingPaint(); return; } // el resumen ya está en memoria: cero POST
      if (!summaryCtx) return; // sin contexto no hay nada que reintentar (el botón no existe aún)
      // retry() solo revive el estado 'error'. Un botón nacido de otro camino
      // caería aquí en un return mudo, que es la misma promesa incumplida que
      // este paquete viene a matar: se arranca un flujo nuevo en su lugar.
      var gen = summaryFlow.retry();
      if (gen == null) gen = summaryFlow.start();
      var pp = interpParts();
      if (pp) {
        hidePendingStates(pp);
        if (pp.status) { pp.status.textContent = t.skeletonStatus || ''; pp.status.removeAttribute('hidden'); }
      }
      fireSummaryFetch(gen);
    }
    // Un solo botón, dos causas. El caso combinado (motor caído + resumen caído)
    // se resuelve en orden: primero el script, después el refetch.
    function wireRetry(p) {
      if (!p.retryBtn || p.retryBtn.getAttribute('data-wired')) return;
      p.retryBtn.setAttribute('data-wired', '1');
      p.retryBtn.addEventListener('click', function () {
        if (!teaserReady()) {
          // Falta el MOTOR, no los datos: recuperarlo no cuesta una ficha del
          // cubo de 10 por IP que comparten los cuatro endpoints.
          teaserTries = 0;
          teaserLoading = false;
          teaserDead = false;
          showInterpLoading();
          whenTeaserReady(summaryFlow.gen(), afterRetryReady, showTeaserUnavailable);
          return;
        }
        afterRetryReady();
      });
    }
    function showSummaryError() {
      hideScanRibbon(); // never claim "done" over a failure
      showPromise(false); // sub-prometer es honesto, sobre-prometer no
      var p = interpParts();
      if (!p) return;
      clearTeaserZone(p.el);
      // Sin motor, el andamiaje de la zona (etiquetas sin cifra, notas que
      // enmarcan cifras, buscador de ciudades) no lo puede llenar nadie.
      if (!teaserReady()) showScaffold(false);
      p.el.removeAttribute('hidden');
      if (p.status) p.status.setAttribute('hidden', '');
      if (p.errMsg) p.errMsg.textContent = t.summaryError || '';
      if (p.retryBtn) {
        p.retryBtn.textContent = t.summaryRetry || T('Reintentar');
        wireRetry(p);
      }
      if (p.errBox) p.errBox.removeAttribute('hidden');
    }

    function cacheKey(p) { return [p.date, p.time, p.lat, p.lon, p.place].join('|'); }
    // key (optional): a write is keyed to the cache's OWN payload, so a response
    // that lands before the lines call of its calculation never augments the
    // previous calculation's cache (lines failed or still in flight).
    function augmentCache(fields, key) {
      try {
        var cr = sessionStorage.getItem('aw_astro_last');
        if (!cr) return;
        var co = JSON.parse(cr);
        if (key && (!co.payload || cacheKey(co.payload) !== key)) return;
        for (var k in fields) { if (Object.prototype.hasOwnProperty.call(fields, k)) co[k] = fields[k]; }
        sessionStorage.setItem('aw_astro_last', JSON.stringify(co));
      } catch (e) {}
    }

    function errorMessage(status) {
      if (status === 422) return t.err422 || t.errInvalid;
      if (status === 400) return t.err400;
      if (status === 429) return t.err429;
      if (status === 502) return t.err502;
      return t.errNetwork;
    }

    // ── scan ribbon (suspense only; promise-driven, never clock-driven) ──
    var scanTimers = [];
    function clearScanTimers() { scanTimers.forEach(clearTimeout); scanTimers = []; }
    function scanEls() {
      var scan = root.querySelector('[data-aw-astro-scan]');
      if (!scan) return null;
      return { scan: scan, text: scan.querySelector('[data-aw-astro-scan-text]'), skip: scan.querySelector('[data-aw-astro-scan-skip]') };
    }
    function startScanRibbon() {
      if (!suspense) return;
      var s = scanEls();
      if (!s) return; // partial sync (assets new, section old): degrade silently
      clearScanTimers();
      if (summaryFlow.state() !== 'pending') { s.scan.hidden = true; return; }
      s.scan.classList.remove('is-done');
      if (s.text) s.text.textContent = scanTextFor('pending', t, false);
      if (s.skip) { s.skip.textContent = t.thSkip || ''; s.skip.hidden = true; }
      s.scan.hidden = false;
      track('astro_theater_start');
      scanTimers.push(setTimeout(function () {
        if (s.skip && !s.scan.hidden && summaryFlow.state() === 'pending') s.skip.hidden = false;
      }, 800));
      // Slow line only for the real slow path (p50 is ~2,5 s; 5 s = tail).
      scanTimers.push(setTimeout(function () {
        if (s.text && summaryFlow.state() === 'pending') s.text.textContent = scanTextFor('pending', t, true);
      }, 5000));
      if (s.skip && !s.skip.getAttribute('data-wired')) {
        s.skip.setAttribute('data-wired', '1');
        s.skip.addEventListener('click', function () {
          track('astro_theater_skip');
          var interpEl = root.querySelector('[data-aw-astro-interp]');
          if (interpEl) { try { interpEl.scrollIntoView({ block: 'start', behavior: RM ? 'auto' : 'smooth' }); } catch (e) {} }
        });
      }
    }
    function endScanRibbon() {
      var s = scanEls();
      if (!s || s.scan.hidden) return;
      clearScanTimers();
      if (s.text) s.text.textContent = scanTextFor('done', t, false);
      s.scan.classList.add('is-done');
      if (s.skip) s.skip.hidden = true;
      scanTimers.push(setTimeout(function () { s.scan.hidden = true; }, 1400));
    }
    function hideScanRibbon() {
      var s = scanEls();
      if (!s) return;
      clearScanTimers();
      s.scan.hidden = true;
    }

    // ── progressive map draw (suspense only) ──
    // The animation RENDERS geometry already computed during a real wait:
    // 10 beats in canonical payload order, ≤3 overlapping, opacity and
    // stroke-dashoffset only. Finalizer removes all staging so chip
    // toggles stay instant and a dead script leaves everything visible.
    var drawTimers = [];
    function clearDrawTimers() { drawTimers.forEach(clearTimeout); drawTimers = []; }
    function runMapDraw() {
      if (!suspense) return;
      var card = root.querySelector('.aw-astro__map-card');
      if (!card) return;
      clearDrawTimers();
      if (RM) { dropNatalMarker(card); return; } // sequence is information, motion is decoration
      var repeat = false;
      try { repeat = !!sessionStorage.getItem('aw_astro_drawn'); } catch (e) {}
      try { sessionStorage.setItem('aw_astro_drawn', '1'); } catch (e) {}
      if (LITE || repeat) {
        // one group fade of 200 ms: the full draw is paid once per session
        card.classList.add('is-fading');
        drawTimers.push(setTimeout(function () {
          card.classList.add('is-faded-in');
          drawTimers.push(setTimeout(function () {
            card.classList.remove('is-fading', 'is-faded-in');
            dropNatalMarker(card);
          }, 260));
        }, 30));
        return;
      }
      card.classList.add('is-drawing');
      var i;
      for (i = 0; i < PLANETS.length; i++) {
        (function (idx) {
          drawTimers.push(setTimeout(function () {
            Array.prototype.forEach.call(card.querySelectorAll('[data-planet="' + idx + '"]'), function (el) {
              el.classList.add('is-in');
            });
          }, 120 + idx * 90));
        })(i);
      }
      // last beat 120 + 9·90 = 930 ms; +260 ms transitions ≈ 1.190 ms end
      drawTimers.push(setTimeout(function () {
        card.classList.remove('is-drawing');
        Array.prototype.forEach.call(card.querySelectorAll('[data-planet].is-in'), function (el) {
          el.classList.remove('is-in');
        });
        dropNatalMarker(card); // ~1.250 ms
      }, 120 + (PLANETS.length - 1) * 90 + 300));
    }

    // ── "naciste aquí" marker (suspense only; client-side coords) ──
    function dropNatalMarker(card) {
      if (!suspense) return;
      if (placeLat == null || placeLon == null) return;
      var svg = card.querySelector('svg');
      if (!svg) return;
      var old = svg.querySelector('[data-aw-astro-me]');
      if (old && old.parentNode) old.parentNode.removeChild(old);
      var x = lonToX(placeLon), y = latToY(placeLat);
      // Name comes from the CHOSEN autocomplete item (correct accents),
      // never from the API echo of the user's typing. Never in analytics.
      var name = (lastPickedName || (placeEl && placeEl.value ? placeEl.value.split(',')[0] : '') || '').trim();
      var g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      g.setAttribute('data-aw-astro-me', '');
      g.setAttribute('class', 'aw-astro__me');
      var anchorEnd = x > 600;
      g.innerHTML =
        '<circle class="aw-astro__me-halo" cx="' + x.toFixed(1) + '" cy="' + y.toFixed(1) + '" r="7"></circle>' +
        '<circle cx="' + x.toFixed(1) + '" cy="' + y.toFixed(1) + '" r="2.6"></circle>' +
        '<text x="' + (anchorEnd ? (x - 9).toFixed(1) : (x + 9).toFixed(1)) + '" y="' + (y + 3.5).toFixed(1) + '"' +
        (anchorEnd ? ' text-anchor="end"' : '') + '>' + esc(name) + '</text>';
      g.style.transformBox = 'fill-box';
      g.style.transformOrigin = 'center';
      svg.appendChild(g);
      if (RM) return;
      g.classList.add('is-entering');
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          g.classList.remove('is-entering');
          g.classList.add('is-entered');
        });
      });
    }

    // ── world view (suspense only; mobile legibility) ──
    // El botón de centrar en la ciudad natal se retiró el 18-sep (founder): el mapa
    // es siempre el mundo entero, y en móvil las líneas engordan para leerse.
    function initMapView() {
      if (!suspense) return;
      var card = root.querySelector('.aw-astro__map-card');
      if (!card) return;
      var mobile = false;
      try { mobile = window.matchMedia('(max-width: 749px)').matches; } catch (e) {}
      card.classList.toggle('is-world-mobile', mobile);
    }

    // Chips are served as the truth (panel 2 redesign): sigla-only text,
    // MC-only default in the markup, no JS ever rewrites chip content.

    // ── angle-set chips (MC + ASC on by default, all toggleable) ──
    var chips = root.querySelectorAll('[data-aw-astro-angle]');
    var mapHint = root.querySelector('[data-aw-astro-maphint]');
    function applyChips() {
      var anyOn = false;
      Array.prototype.forEach.call(chips, function (chip) {
        var code = chip.getAttribute('data-aw-astro-angle');
        var on = chip.classList.contains('is-on');
        if (on) anyOn = true;
        chip.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (!linesEl) return;
        var g = linesEl.querySelector('[data-angle-group="' + code + '"]');
        if (g) g.style.display = on ? '' : 'none';
      });
      // All chips off: say so instead of showing a silently empty map.
      if (mapHint) mapHint.hidden = anyOn;
    }
    Array.prototype.forEach.call(chips, function (chip) {
      chip.addEventListener('click', function () {
        chip.setAttribute('data-aw-user-toggled', '1');
        chip.classList.toggle('is-on');
        applyChips();
      });
    });

    // ── line legend (same color constants as the drawn lines) ──
    // Angle rows FIRST (the novice's real question is the four siglas, not
    // the ten planets), then the planet grid — which is the single owner of
    // planet themes now that the data rows dropped their duplicate gloss.
    var legendEl = root.querySelector('[data-aw-astro-legend]');
    if (legendEl) {
      var lgHtml = '<ul class="aw-astro__angle-key">' +
        ANGLES.map(function (a) {
          return '<li><b>' + angleLabel(a.code) + '</b> · ' + esc(a.name) + ' · ' + esc(a.micro) + '</li>';
        }).join('') + '</ul>';
      lgHtml += '<h2 class="aw-astro__legend-title">' + esc(LEGEND_TITLE) + '</h2><ul class="aw-astro__legend-grid">';
      PLANETS.forEach(function (p) {
        lgHtml += '<li><i class="aw-astro__legend-swatch" style="background:' + p.color + '"></i>' +
          '<span class="aw-astro__legend-glyph" style="color:' + p.color + '">' + p.glyph + '︎</span>' +
          '<b>' + esc(T(p.es)) + '</b> ' + esc(T(p.short)) + '</li>';
      });
      lgHtml += '</ul>';
      legendEl.innerHTML = lgHtml;
    }

    // ── render the live response ──────────────────────────────
    function renderResult(data, placeText, dateText, timeText) {
      var raw = (data.lines && data.lines.meridian_horizon) || [];
      shareLines = data;
      mountShare();

      root.querySelector('[data-aw-astro-result-lead]').textContent =
        fmt(t.resultLeadTpl, { place: placeText, date: dateText, time: timeText });

      // Group by planet: byPlanet[planetIndex] = { MC: lon, IC: lon, ... }
      var byPlanet = {};
      raw.forEach(function (l) {
        var p = planetFor(l.planet);
        var code = ANGLE_ALIAS[String(l.angle || '').toUpperCase()];
        if (!p || !code || l.lon == null || isNaN(Number(l.lon))) return;
        var idx = PLANETS.indexOf(p);
        if (!byPlanet[idx]) byPlanet[idx] = {};
        byPlanet[idx][code] = Number(l.lon);
      });

      // ASC/DSC horizon curves, when the API sends them:
      // curveBy[planetIndex] = { ASC: points, DSC: points }.
      var curveBy = {};
      var rawCurves = (data.lines && data.lines.horizon_curves) || [];
      rawCurves.forEach(function (c) {
        var p = planetFor(c.planet);
        var code = ANGLE_ALIAS[String(c.angle || '').toUpperCase()];
        if (!p || !code || !c.points || c.points.length < 2) return;
        var idx = PLANETS.indexOf(p);
        if (!curveBy[idx]) curveBy[idx] = {};
        curveBy[idx][code] = c.points;
      });

      // SVG lines: one <g> per angle set so the chips can toggle whole sets.
      // MC/IC are vertical meridian lines. ASC/DSC draw the horizon curve when
      // available and FALL BACK to the vertical meridian line when it is not,
      // so the map keeps working while horizon_curves is not live.
      if (linesEl) {
        // data-planet feeds the per-planet draw beats (harmless flag-off);
        // pathLength="1" lets CSS draw MC lines by dashoffset without
        // measuring.
        var svg = '';
        ANGLES.forEach(function (a) {
          var parts = '';
          PLANETS.forEach(function (p, idx) {
            var lon = byPlanet[idx] ? byPlanet[idx][a.code] : null;
            var curve = curveBy[idx] ? curveBy[idx][a.code] : null;
            var labelX = null;
            if (curve) {
              var paths = curveToPaths(curve);
              if (paths.length) {
                paths.forEach(function (d) {
                  parts += '<path data-planet="' + idx + '" d="' + d + '" fill="none" stroke="' + p.color + '" stroke-width="' + a.width + '" stroke-opacity="0.9"' +
                    (a.dash ? ' stroke-dasharray="' + a.dash + '"' : '') + '></path>';
                });
                // Points come ordered by ascending lat: label the endpoint
                // nearest this set's label band, clamped inside the map.
                var end = curve[a.labelTop ? curve.length - 1 : 0];
                var endLon = Array.isArray(end) ? end[1] : end.lon;
                labelX = Math.min(MAP_W - 8, Math.max(8, lonToX(endLon)));
              }
            }
            if (labelX == null && lon != null) {
              var x = lonToX(lon);
              parts += '<line data-planet="' + idx + '" pathLength="1" x1="' + x.toFixed(1) + '" y1="' + MAP_TOP + '" x2="' + x.toFixed(1) + '" y2="' + MAP_BOTTOM + '"' +
                ' stroke="' + p.color + '" stroke-width="' + a.width + '" stroke-opacity="0.9"' +
                (a.dash ? ' stroke-dasharray="' + a.dash + '"' : '') + '></line>';
              labelX = x;
            }
            if (labelX == null) return;
            parts += '<text data-planet="' + idx + '" x="' + labelX.toFixed(1) + '" y="' + (a.labelTop ? LABEL_TOP_Y : LABEL_BOTTOM_Y) + '"' +
              ' text-anchor="middle" font-family="Gelasio, serif" font-size="14" fill="' + p.color + '">' +
              p.glyph + '︎</text>';
          });
          svg += '<g data-angle-group="' + a.code + '">' + parts + '</g>';
        });
        linesEl.innerHTML = svg;
        applyChips();
      }

      // "Tus líneas principales": compact audit trail, one row per planet.
      // The per-row meaning gloss is gone (the legend above owns planet
      // themes); longitudes align in a 2-column micro-grid, tabular.
      if (listEl) {
        var html = '';
        PLANETS.forEach(function (p, idx) {
          if (!byPlanet[idx]) return;
          var angleBits = [];
          ANGLES.forEach(function (a) {
            if (byPlanet[idx][a.code] == null) return;
            angleBits.push('<span class="aw-astro__pl-angle"><b>' + angleLabel(a.code) + '</b> ' + esc(fmtLon(byPlanet[idx][a.code])) + '</span>');
          });
          if (!angleBits.length) return;
          html += '<div class="aw-astro__pl-row">' +
            '<span class="aw-astro__pl-glyph" style="color:' + p.color + '">' + p.glyph + '︎</span>' +
            '<div class="aw-astro__pl-body">' +
            '<h3>' + esc(T(p.es)) + '</h3>' +
            '<p class="aw-astro__pl-angles">' + angleBits.join('') + '</p>' +
            '</div></div>';
        });
        listEl.innerHTML = html;
      }
    }

    // ── wiring ────────────────────────────────────────────────
    Array.prototype.forEach.call(root.querySelectorAll('[data-aw-astro-go]'), function (btn) {
      btn.addEventListener('click', function () {
        var target = btn.getAttribute('data-aw-astro-go');
        var v = btn.getAttribute('data-aw-astro-validate');
        if (v === 'date' && !validDate()) { return; }
        if (v === 'time' && !validTime()) { return; }
        go(target);
      });
    });
    var backBtn = root.querySelector('[data-aw-astro-back]');
    if (backBtn) backBtn.addEventListener('click', back);

    // place autocomplete wiring
    if (placeEl && suggestEl) {
      placeEl.addEventListener('input', function () {
        placeLat = null; placeLon = null; placePicked = false; clearError();
        var q = placeEl.value.trim();
        if (acTimer) clearTimeout(acTimer);
        if (q.length < 3) { clearSuggest(); return; }
        acTimer = setTimeout(function () { fetchAutocomplete(q); }, 350);
      });
      placeEl.addEventListener('blur', function () { setTimeout(clearSuggest, 150); });
      // mousedown (not click) so the pick lands before the input's blur fires
      suggestEl.addEventListener('mousedown', function (e) {
        var li = e.target.closest('[data-i]'); if (!li) return;
        e.preventDefault();
        pickSuggestion(acItems[parseInt(li.getAttribute('data-i'), 10)]);
      });
      // click too: assistive tech can fire click without a mousedown; after
      // a pick acItems is cleared, so a duplicate invocation no-ops.
      suggestEl.addEventListener('click', function (e) {
        var li = e.target.closest('[data-i]'); if (!li) return;
        pickSuggestion(acItems[parseInt(li.getAttribute('data-i'), 10)]);
      });
      placeEl.addEventListener('keydown', function (e) {
        if (suggestEl.hidden || !acItems.length) return;
        if (e.key === 'ArrowDown') { e.preventDefault(); moveActive(1); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); moveActive(-1); }
        else if (e.key === 'Enter') {
          if (acActive > -1) { e.preventDefault(); pickSuggestion(acItems[acActive]); }
        } else if (e.key === 'Escape') { clearSuggest(); }
      });
    }
    var linesFold = root.querySelector('[data-aw-astro-lines-fold]');
    if (linesFold) linesFold.addEventListener('toggle', function () {
      if (linesFold.open) track('astro_lines_open'); // structure only
    });
    Array.prototype.forEach.call(root.querySelectorAll('[data-aw-astro-why]'), function (btn) {
      btn.addEventListener('click', function () {
        var box = root.querySelector('#' + btn.getAttribute('data-aw-astro-why'));
        if (box) box.classList.toggle('is-open');
      });
    });
    if (submitBtn) {
      submitBtn.setAttribute('data-label', submitBtn.textContent);
      submitBtn.addEventListener('click', calculate);
    }

    // Si el usuario ya tocó "Calcula tu mapa" antes de que cargara este script
    // (pre-step inline de la sección), arrancar en la fecha: go('date') emite
    // astromapa_start y astro_step_date una sola vez porque started aún es false.
    window.__awAstroReady = true;
    if (window.__awAstroPreStep === 'date') { window.__awAstroPreStep = null; go('date'); return; }

    // Resultado persistente: si hay un cálculo de hace menos de 30 minutos en
    // este navegador, se vuelve a pintar sin llamar a la API (volver atrás desde
    // la landing ya no devuelve un formulario vacío). Sin astromapa_start ni
    // astromapa_complete: evento propio astro_result_restored.
    var restored = false;
    try {
      var cacheRaw = sessionStorage.getItem('aw_astro_last');
      var cache = cacheRaw ? JSON.parse(cacheRaw) : null;
      if (cache && cache.data && cache.ts && (Date.now() - cache.ts) < 30 * 60 * 1000) {
        renderResult(cache.data, cache.placeText || '', cache.dateText || '', cache.timeText || '');
        var jumpR = root.querySelector('[data-aw-astro-jump]');
        if (jumpR) jumpR.removeAttribute('hidden');
        started = true;
        go('result');
        runMapDraw();
        initMapView();
        var noteEl = root.querySelector('[data-aw-astro-restored]');
        if (noteEl) noteEl.removeAttribute('hidden');
        var pr = interpParts();
        var fullCache = !!(cache.summary && cache.lib && cache.payload);
        if (pr && cache.payload) {
          summaryCtx = { payload: cache.payload, endpoint: pr.el.getAttribute('data-aw-astro-basic-endpoint'), libUrl: pr.el.getAttribute('data-aw-astro-lib') };
          lastPickedName = cache.cityName || '';
          // Esta rama corre DENTRO de initAstro, y con defer el primer script se
          // ejecuta con readyState 'interactive': aquí window.AWAstroTeaser
          // todavía no existe por construcción. Por eso se espera al motor del
          // teaser. Lo que cambia
          // respecto de antes es el final: pasados los 5 s ya no hay return mudo,
          // se entrega a applySummary, que degrada con honestidad.
          (function paintWhenReady(tries) {
            if (!teaserReady() && tries < 50) { setTimeout(function () { paintWhenReady(tries + 1); }, 100); return; }
            if (fullCache) { applySummary(cache.summary, cache.lib); return; }
            // Caché a medias: aw_astro_last se escribe al pintar el mapa y el
            // resumen se añade después (augmentCache). Una recarga en esa
            // ventana, o un resumen que falló, dejaba la promesa en pie con la
            // zona oculta detrás: el síntoma exacto del 6-sep, sin caja de
            // error y sin reintento. El flujo normal vuelve a gobernarla.
            startSummary(cache.payload);
            showTeaserSkeleton();
          })(0);
        } else if (pr) {
          showPromise(false); // caché sin datos de nacimiento: no hay nada que prometer
        }
        track('astro_result_restored');
        restored = true;
      }
    } catch (e) { restored = false; }
    // Con relleno válido NO se pasa por la intro (ver aw-prefill-boot: el teatro
    // ya está pintado desde el primer frame y la intro lo haría parpadear).
    // «Abrir en Chrome» (24-sep): la marca se lee y sale de la barra antes del relleno, y
    // solo vale con su propio relleno completo tras el #.
    if (window.AWShare && typeof window.AWShare.shareEscapeDesign === 'function') {
      shareEscape = window.AWShare.shareEscapeDesign(window.location.hash, ['foto', 'instrumento', 'pliego']);
      if (shareEscape) window.AWShare.dropShareKey();
    }
    var pre = window.AWPrefill ? window.AWPrefill.read(window.location.search, window.location.hash) : null;
    var prefilled = !!(pre && window.AWPrefill.fill(root, 'aw-astro', pre.a, null));
    if (!restored && !prefilled) go('intro');

    // ── relleno desde la URL ────────────────────────────────────────────
    // Un asistente que ya conoce fecha, hora y lugar entrega un enlace y esto
    // arranca el calculo solo, en vez de mandar a rellenar tres pantallas.
    // Cae en la pantalla de CALCULO, no en el resultado, asi que la puerta de
    // email aparece igual que si lo hubiera tecleado una persona. AWPrefill
    // borra los parametros de la barra al leerlos: los datos de nacimiento no
    // se quedan en el historial ni viajan en el Referer.
    if (prefilled) {
      if (pre.a.lat != null && pre.a.lon != null) { placeLat = pre.a.lat; placeLon = pre.a.lon; placePicked = true; }
      window.AWPrefill.scrub();
      track('astromapa_prefill');
      // Los scripts van con defer y este corre ANTES que aw-theater.js y aw-gate.js:
      // calcular ya mismo dejaba theaterOn() en falso y el relleno saltaba del
      // primer frame a la puerta en ~1 s, sin animación. Se espera al final del
      // parseo (DOMContentLoaded llega cuando ya corrieron todos los defer).
      var runPrefill = function () {
        if (runPrefill.done) return;
        runPrefill.done = true;
        calculate();
      };
      if (window.AWTheater || document.readyState === 'complete') runPrefill();
      else {
        document.addEventListener('DOMContentLoaded', runPrefill);
        window.addEventListener('load', runPrefill);
      }
    }
    // La marca de «Abrir en Chrome» solo vale con su propio relleno completo tras el #
    // (el cálculo es asíncrono: queda fijada antes de que se decida la puerta).
    if (!(prefilled && pre.desdeHash) || restored) shareEscape = '';
  }

  // Node test surface (browser flow untouched): pure helpers only.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { createSummaryFlow: createSummaryFlow, scanTextFor: scanTextFor, _norm: norm, _curveToPaths: curveToPaths, _trCopy: trCopy, _fmtLon: fmtLon, _angleLabel: angleLabel };
  }

  if (typeof document !== 'undefined') {
    // Single source of truth for planet colors/glyphs and angle dashes:
    // the teaser's unit badges read THIS, never a duplicated table (a
    // future color change must stay in sync with the drawn map).
    try { window.AWAstroMap = { PLANETS: PLANETS, ANGLES: ANGLES }; } catch (e) {}

    // Guardrail G10 signal: structure-only, fires only for this feature's files.
    try {
      window.addEventListener('error', function (e) {
        var f = (e && e.filename) || '';
        // aw-astromapa*.js
        if (f.indexOf('aw-astromapa') !== -1 || f.indexOf('aw-teaser') !== -1) track('astro_js_error');
      });
    } catch (e) {}

    var readyFn = function () {
      Array.prototype.forEach.call(document.querySelectorAll('[data-aw-astro]'), initAstro);
    };
    if (document.readyState !== 'loading') readyFn();
    else document.addEventListener('DOMContentLoaded', readyFn);
  }
})();
